import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import SpeedIcon from '@mui/icons-material/Speed';
import api from '../../services/api';
import type { RadiusLookupResult, TrafficFlowReading } from '../../types/radius';

const TRAFFIC_POLL_DELAY_MS = 45000;

function formatBps(bps: number | null): string {
  if (bps == null) return '—';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(1)} kbps`;
  return `${bps} bps`;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(2)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
}

/** Plan's provisioned rate from the groupname convention 'supa-{up}-{down}' (Mbps),
 * compared against the router's actual max-limit — a mismatch indicates a rate-limit
 * config bug, not a connectivity fault. */
function planRateBps(groupname: string | null): { up: number; down: number } | null {
  if (!groupname) return null;
  const parts = groupname.split('-');
  if (parts.length !== 3) return null;
  const up = Number(parts[1]);
  const down = Number(parts[2]);
  if (!Number.isFinite(up) || !Number.isFinite(down)) return null;
  return { up: up * 1_000_000, down: down * 1_000_000 };
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(startIso: string | null, stopIso: string | null): string {
  if (!startIso) return '—';
  const start = new Date(startIso).getTime();
  const end = stopIso ? new Date(stopIso).getTime() : Date.now();
  const minutes = Math.max(0, Math.round((end - start) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return `${hours}h ${remMinutes}m`;
}

const SESSION_STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  online: { label: 'Online', color: 'success' },
  unknown: { label: 'Stale session', color: 'warning' },
  offline: { label: 'Offline', color: 'default' },
};

type TrafficVerdict = { severity: 'success' | 'warning' | 'info'; label: string; detail: string };

/** A RADIUS session can show "connected" indefinitely even on a dead link — nothing
 * tears it down automatically. The instantaneous `rate` alone can't distinguish idle
 * from stalled, so the real signal is whether cumulative bytes moved at all between
 * two readings taken ~30-60s apart. */
function computeTrafficVerdict(first: TrafficFlowReading | null, second: TrafficFlowReading | null): TrafficVerdict | null {
  if (!first) return null;
  if (!first.found || !first.stats) {
    return {
      severity: 'info',
      label: 'No matching queue found on the router',
      detail: 'Cannot verify traffic flow this way — the dynamic queue naming assumption may not hold for this session.',
    };
  }
  if (!second) {
    const instantRate = (first.stats.rate_up_bps || 0) + (first.stats.rate_down_bps || 0);
    return {
      severity: 'info',
      label: instantRate > 0 ? 'Traffic detected — confirming…' : 'No instantaneous traffic — confirming…',
      detail: `Waiting ~${Math.round(TRAFFIC_POLL_DELAY_MS / 1000)}s for a second reading to confirm whether bytes are actually accumulating.`,
    };
  }
  if (!second.found || !second.stats) {
    return { severity: 'info', label: 'Second reading failed', detail: 'Could not re-read the queue for comparison.' };
  }
  const firstTotal = (first.stats.bytes_up || 0) + (first.stats.bytes_down || 0);
  const secondTotal = (second.stats.bytes_up || 0) + (second.stats.bytes_down || 0);
  const deltaBytes = secondTotal - firstTotal;
  const elapsedSec = Math.max(1, Math.round((new Date(second.read_at).getTime() - new Date(first.read_at).getTime()) / 1000));
  if (deltaBytes > 0) {
    return { severity: 'success', label: 'Actively passing traffic', detail: `+${formatBytes(deltaBytes)} over ${elapsedSec}s.` };
  }
  return {
    severity: 'warning',
    label: 'Connected but no traffic observed',
    detail: `Zero byte movement over ${elapsedSec}s despite an open session — likely stalled, worth escalating.`,
  };
}

export default function RadiusLookupPage() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RadiusLookupResult | null>(null);

  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficPolling, setTrafficPolling] = useState(false);
  const [trafficError, setTrafficError] = useState<string | null>(null);
  const [trafficFirst, setTrafficFirst] = useState<TrafficFlowReading | null>(null);
  const [trafficSecond, setTrafficSecond] = useState<TrafficFlowReading | null>(null);
  const trafficTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (trafficTimerRef.current) clearTimeout(trafficTimerRef.current);
  }, []);

  const canSubmit = username.trim().length > 0;

  const resetTraffic = () => {
    if (trafficTimerRef.current) clearTimeout(trafficTimerRef.current);
    setTrafficFirst(null);
    setTrafficSecond(null);
    setTrafficError(null);
    setTrafficLoading(false);
    setTrafficPolling(false);
  };

  const handleLookup = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setResult(null);
    resetTraffic();
    try {
      const data = await api.adminGetRadiusLookup(username.trim());
      setResult(data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'RADIUS lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUsername('');
    setResult(null);
    setError(null);
    resetTraffic();
  };

  const handleCheckTrafficFlow = async () => {
    if (!result?.connection_type) return;
    const assignedIp = result.session?.assigned_ip || null;
    if (result.connection_type === 'ipoe' && !assignedIp) {
      setTrafficError('No assigned IP on the current session — cannot look up a queue.');
      return;
    }
    resetTraffic();
    setTrafficLoading(true);
    try {
      const first = await api.adminGetTrafficFlow({
        connectionType: result.connection_type,
        username: result.username,
        assignedIp,
      });
      setTrafficFirst(first);
      setTrafficLoading(false);
      if (!first.found) return;
      setTrafficPolling(true);
      trafficTimerRef.current = setTimeout(async () => {
        try {
          const second = await api.adminGetTrafficFlow({
            connectionType: result.connection_type!,
            username: result.username,
            assignedIp,
          });
          setTrafficSecond(second);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { detail?: string } }; message?: string };
          setTrafficError(err?.response?.data?.detail || err?.message || 'Second traffic-flow check failed');
        } finally {
          setTrafficPolling(false);
        }
      }, TRAFFIC_POLL_DELAY_MS);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setTrafficError(err?.response?.data?.detail || err?.message || 'Traffic-flow check failed');
      setTrafficLoading(false);
    }
  };

  const sessionStatus = result?.session ? SESSION_STATUS_CHIP[result.session.status] : SESSION_STATUS_CHIP.offline;
  const trafficVerdict = computeTrafficVerdict(trafficFirst, trafficSecond);
  const latestTraffic = trafficSecond || trafficFirst;
  const planRate = result ? planRateBps(result.groupname) : null;
  const maxLimitMismatch = !!(
    planRate && latestTraffic?.stats &&
    latestTraffic.stats.max_limit_up_bps != null && latestTraffic.stats.max_limit_down_bps != null &&
    (latestTraffic.stats.max_limit_up_bps !== planRate.up || latestTraffic.stats.max_limit_down_bps !== planRate.down)
  );
  const dropPct = (() => {
    if (!latestTraffic?.stats) return null;
    const dropped = (latestTraffic.stats.dropped_up || 0) + (latestTraffic.stats.dropped_down || 0);
    const packets = (latestTraffic.stats.packets_up || 0) + (latestTraffic.stats.packets_down || 0);
    const total = dropped + packets;
    return total > 0 ? (dropped / total) * 100 : 0;
  })();

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        RADIUS Lookup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Confirm a subscriber's plan, auth configuration, and session status on the RADIUS server.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <SectionLabel>Subscriber</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Username"
              placeholder="e.g. SAS0000066856"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              fullWidth
              size="small"
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleLookup}
              disabled={!canSubmit || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <RouterIcon />}
              sx={{ height: 40 }}
            >
              {loading ? 'Looking up…' : 'Lookup'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={loading || (!username && !result && !error)}
              sx={{ height: 40 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {result && !result.found && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No RADIUS profile found for <strong>{result.username}</strong> — it may not have been provisioned yet.
        </Alert>
      )}

      {result && result.found && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <SectionLabel>Profile</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Plan</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {result.groupname || '—'}{result.plan_label ? ` (${result.plan_label})` : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Connection type</Typography>
                <Chip label={result.connection_type === 'pppoe' ? 'PPPoE' : 'IPoE'} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Auth attributes</Typography>
                {result.auth_attributes.map((a, i) => (
                  <Typography key={i} variant="body2">
                    {a.attribute} {a.op} {a.value}
                  </Typography>
                ))}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">MAC address</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {result.mac_address || '—'}
                </Typography>
                {result.mac_source && (
                  <Typography variant="caption" color="text.secondary">
                    via {result.mac_source === 'router_lease' ? 'RouterOS DHCP lease' : 'RADIUS session'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Hostname</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {result.hostname || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <SectionLabel>Session status</SectionLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Chip label={sessionStatus.label} color={sessionStatus.color} size="small" />
              {result.session?.source === 'router_lease' && (
                <Typography variant="caption" color="text.secondary">
                  via RouterOS DHCP lease — no RADIUS accounting session exists for this username
                </Typography>
              )}
            </Box>
            {result.session ? (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Assigned IP</Typography>
                  <Typography variant="body2">{result.session.assigned_ip || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">NAS</Typography>
                  <Typography variant="body2">{result.session.nas_ip_address || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Connected since</Typography>
                  <Typography variant="body2">{formatDateTime(result.session.connected_since)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Online for</Typography>
                  <Typography variant="body2">
                    {result.session.online_minutes != null ? `${result.session.online_minutes} min` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Uploaded</Typography>
                  <Typography variant="body2">{result.session.upload_mb != null ? `${result.session.upload_mb} MB` : '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Downloaded</Typography>
                  <Typography variant="body2">{result.session.download_mb != null ? `${result.session.download_mb} MB` : '—'}</Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">Not currently connected.</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <SectionLabel>Traffic Flow</SectionLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              RADIUS showing "connected" only proves a session is open — it doesn't prove traffic is moving. This
              reads the router's live queue counters directly and takes two readings ~45s apart to confirm bytes
              are actually accumulating, not just that the instantaneous rate happened to be zero.
            </Typography>

            {!result.session ? (
              <Typography variant="body2" color="text.secondary">Not currently connected — nothing to check.</Typography>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCheckTrafficFlow}
                  disabled={trafficLoading || trafficPolling}
                  startIcon={(trafficLoading || trafficPolling) ? <CircularProgress size={16} /> : <SpeedIcon />}
                  sx={{ mb: 2 }}
                >
                  {trafficLoading ? 'Checking…' : trafficPolling ? 'Confirming…' : trafficFirst ? 'Check again' : 'Check Traffic Flow'}
                </Button>

                {trafficError && <Alert severity="error" sx={{ mb: 2 }}>{trafficError}</Alert>}

                {trafficVerdict && (
                  <Alert severity={trafficVerdict.severity} sx={{ mb: 2 }}>
                    <strong>{trafficVerdict.label}</strong> — {trafficVerdict.detail}
                  </Alert>
                )}

                {latestTraffic?.stats && (
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Current rate (up/down)</Typography>
                      <Typography variant="body2">
                        {formatBps(latestTraffic.stats.rate_up_bps)} / {formatBps(latestTraffic.stats.rate_down_bps)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Session total (up/down)</Typography>
                      <Typography variant="body2">
                        {formatBytes(latestTraffic.stats.bytes_up)} / {formatBytes(latestTraffic.stats.bytes_down)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Dropped packets</Typography>
                      <Typography variant="body2">
                        {dropPct != null ? `${dropPct.toFixed(2)}%` : '—'}
                        {dropPct != null && dropPct > 0.5 && (
                          <Chip label="Elevated" color="warning" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Max limit (up/down)</Typography>
                      <Typography variant="body2">
                        {formatBps(latestTraffic.stats.max_limit_up_bps)} / {formatBps(latestTraffic.stats.max_limit_down_bps)}
                        {maxLimitMismatch && (
                          <Chip label="Mismatch vs plan" color="warning" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      {planRate && (
                        <Typography variant="caption" color="text.secondary">
                          Plan: {planRate.up / 1_000_000}/{planRate.down / 1_000_000} Mbps
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">Queue</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{latestTraffic.stats.queue_name || '—'}</Typography>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <SectionLabel>Recent sessions</SectionLabel>
            {result.recent_sessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No session history found.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Started</TableCell>
                    <TableCell>Stopped</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>NAS</TableCell>
                    <TableCell>Up / Down</TableCell>
                    <TableCell>Terminate cause</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.recent_sessions.map((s, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.started_at)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {s.is_ongoing ? <Chip label="Ongoing" color="success" size="small" /> : formatDateTime(s.stopped_at)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDuration(s.started_at, s.stopped_at)}</TableCell>
                      <TableCell>{s.assigned_ip || '—'}</TableCell>
                      <TableCell>{s.nas_ip_address || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {s.upload_mb != null ? `${s.upload_mb} MB` : '—'} / {s.download_mb != null ? `${s.download_mb} MB` : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.terminate_cause || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
